// todo remove once MOJ-17070 delivered
// The only difference from the Mojito implementation is in the renderStream() method
import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';

import VirtualStreamModes from './types.js';

const log = MojitoCore.logger.get('VirtualStream');
const {resizeDetector} = MojitoCore.Base.resizeDetector;
const {UIViewImplementation} = MojitoCore.Presentation;
const {MOBILE} = MojitoCore.Services.SystemSettings.types.APPLICATION_MODE;

const {Button, Image, IframeViewer} = MojitoPresentation.Components;
const isShallowEqual = MojitoCore.Base.objUtils.isShallowEqual;
const fullscreenChangeEvents = ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange'];

export class VirtualStream extends UIViewImplementation {
    constructor(props) {
        super(props);

        this.state = {
            isWatchRequested: this.appContext().systemSettings().applicationMode !== MOBILE,
        };
        this.containerEl = null;

        this.onContainerRefChanged = this.onContainerRefChanged.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onWatchClick = this.onWatchClick.bind(this);
        this.onFullscreenChanged = this.onFullscreenChanged.bind(this);
    }

    componentDidMount() {
        fullscreenChangeEvents.forEach(eventType =>
            document.addEventListener(eventType, this.onFullscreenChanged, false)
        );
    }

    componentWillUnmount() {
        fullscreenChangeEvents.forEach(eventType =>
            document.removeEventListener(eventType, this.onFullscreenChanged, false)
        );
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !isShallowEqual(this.props, nextProps) || !isShallowEqual(this.state, nextState);
    }

    onContainerRefChanged(el) {
        if (this.containerEl) {
            resizeDetector.unlisten(this.containerEl);
        }
        this.containerEl = el;

        if (this.containerEl) {
            resizeDetector.listen(this.containerEl, this.onResize);
            this.onResize();
        }
    }

    onFullscreenChanged() {
        // Call onResize to see if we have to update mode after closing fullscreen. Needed if e.g. mobile screen is
        // rotated while in fullscreen.
        this.onResize();
    }

    isFullscreen() {
        return !!(document.fullscreenElement || document.webkitFullscreenElement);
    }

    onResize() {
        if (this.isFullscreen()) {
            // Preventing mode to change if screen is rotated while in fullscreen.
            return;
        }

        const mode =
            this.containerEl.clientWidth > this.config.maxCompactModeWidth
                ? VirtualStreamModes.MODES.WIDE
                : VirtualStreamModes.MODES.COMPACT;
        if (this.state.mode !== mode) {
            this.setState({mode});
        }
    }

    onWatchClick() {
        this.emitAnalytics('virtualStreamingWatchButtonClicked', this.props.sportId);
        this.setState({
            isWatchRequested: true,
        });
    }

    renderStream() {
        const iframeViewerConfig =
            this.state.mode === VirtualStreamModes.MODES.COMPACT
                ? this.props.channelId
                    ? this.config.withChannelCompactIframeViewer || this.config.compactIframeViewer
                    : this.config.compactIframeViewer
                : this.props.channelId
                  ? this.config.withChannelIframeViewer || this.config.iframeViewer
                  : this.config.iframeViewer;
        const config = {...iframeViewerConfig, style: this.style.iframe[this.state.mode]};
        const dataMap = {
            sportId: this.props.sportId,
            sportClassId: this.props.sportClassId,
            eventId: this.props.eventId,
            channelId: this.props.channelId,
        };

        return <IframeViewer config={config} dataMap={dataMap} />;
    }

    renderPreStream() {
        return (
            <div style={this.config.style.buttonContainer}>
                <Button onClick={this.onWatchClick} config={this.config.button}>
                    <Image config={this.config.buttonIcon} />
                    {this.resolveString('$VIRTUAL_STREAM.WATCH')}
                </Button>
            </div>
        );
    }

    render() {
        if (!this.props.eventId && !this.props.sportId && !this.props.channelId) {
            log.error(
                "Streaming setup is missing required parameters: 'eventId', 'sportId', or 'channelId'. Please check the configuration."
            );

            return null;
        }
        const streamState = this.state.isWatchRequested ? this.renderStream() : this.renderPreStream();

        return (
            <div
                className="virtual-stream"
                style={this.style.container[this.state.mode]}
                ref={this.onContainerRefChanged}
            >
                {streamState}
            </div>
        );
    }
}

VirtualStream.getStyle = function (config, mode, merge) {
    const generateAllModesStylePack = (modeDictionary, baseStyles) => {
        return Object.values(modeDictionary).reduce((acc, mode) => {
            acc[mode] = merge(baseStyles, config.style.modes[mode]);

            return acc;
        }, {});
    };

    return {
        container: generateAllModesStylePack(VirtualStreamModes.MODES, config.style.container),
        iframe: generateAllModesStylePack(VirtualStreamModes.MODES, config.style.iframe),
    };
};
